import { CommonModule } from '@angular/common';
import {
    Component,
    computed,
    effect,
    inject,
    input,
    InputSignal,
    Signal,
    viewChild,
} from '@angular/core';
import {
    type CellStyle,
    Cell,
    EventObject,
    Graph,
    HierarchicalLayout,
    InternalEvent,
    InternalMouseEvent,
    Morphing,
} from '@maxgraph/core';
import { TriggerEventType } from '../../../models';
import {
    EventMap,
    IqBuilderAuditToolDashboardEventsService,
} from './iq-builder-audit-tool-dashboard-events.service';

enum DIRECTION {
    NORTH = 'north',
    SOUTH = 'south',
    EAST = 'east',
    WEST = 'west',
}

@Component({
    selector: 'iq-builder-diagram-builder',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './diagram-builder.component.html',
})
export class DiagramBuilderComponent {
    graph: Graph;

    private _service: IqBuilderAuditToolDashboardEventsService = inject(
        IqBuilderAuditToolDashboardEventsService,
    );

    title: InputSignal<string> = input();
    triggerEvents: InputSignal<TriggerEventType> = input({});
    diagramContainer: Signal<any> = viewChild<any>('diagramContainer');

    showCard: Signal<boolean> = computed(() => {
        const graph: EventMap = this._service.createEventMap(
            this.triggerEvents(),
        );

        return graph.nodes.length > 0;
    });

    constructor() {
        effect(() => {
            const graph: EventMap = this._service.createEventMap(
                this.triggerEvents(),
            );

            if (!this.showCard()) return;

            this.graph?.destroy();

            this.renderDiagram(this.diagramContainer().nativeElement, graph);
        });
    }

    renderDiagram(container: HTMLElement, data: EventMap) {
        // Disables the built-in context menu
        InternalEvent.disableContextMenu(container);

        const nodeMap = new Map<string, Cell>();

        this.graph = getGraph(container);
        const hierarchicalLayout = new HierarchicalLayout(
            this.graph,
            DIRECTION.WEST,
            true,
        );

        this.graph.setPanning(false); // Use mouse right button for panning
        // Gets the default parent for inserting new cells. This
        // is normally the first child of the root (ie. layer 0).
        const parent = this.graph.getDefaultParent();
        this.graph.removeCells(null, true);

        // Adds cells to the model in a single step
        this.graph.batchUpdate(() => {
            data.nodes.forEach((node) => {
                const vertex = this.graph.insertVertex({
                    parent,
                    position: [10, 0],
                    size: [380, 50],
                    value: node.label,
                    style: this.getDefaultVertexStyle(),
                });
                nodeMap.set(node.id, vertex);
            });

            data.edges.forEach((edge) => {
                const source = nodeMap.get(edge.source);
                const target = nodeMap.get(edge.target);

                this.graph.insertEdge({
                    parent,
                    source,
                    target,
                    style: this.getDefaultEdgeStyle(),
                });
            });
        });

        this.executeLayout(this.graph, hierarchicalLayout, null, null);

        this.graph.addListener(
            InternalEvent.CLICK,
            (sender: EventDiagramGraph, evt: EventObject) => {
                this.updateHighlightCells(evt);
            },
        );
    }

    private getDefaultEdgeStyle(): CellStyle {
        return {
            edgeStyle: 'orthogonalEdgeStyle',
            rounded: true,
            strokeColor: '#192129',
        };
    }
    private getHighlightedEdgeStyle(): CellStyle {
        return {
            edgeStyle: 'orthogonalEdgeStyle',
            rounded: true,
            strokeColor: '#0046e0',
        };
    }

    private getDefaultVertexStyle() {
        return {
            rounded: true,
            fontFamily: 'Sequel Sans',
            fillColor: '#e9ecf1',
            fontSize: 14,
            strokeColor: '#e9ecf1',
        };
    }
    private getHiglightedVertexStyle(): CellStyle {
        return {
            rounded: true,
            fontFamily: 'Sequel Sans',
            fillColor: '#0046e0',
            fontSize: 14,
            strokeColor: '#0046e0',
            fontColor: '#ffffff',
        };
    }

    private updateHighlightCells(evt: EventObject) {
        const currentCell: Cell = evt.getProperty('cell');

        if (!currentCell) return;

        const oldCells: Cell[] = this.graph.getCells(0, 0, 100000, 100000);

        oldCells.forEach((cell) => {
            if (cell.isVertex()) {
                cell.setStyle(this.getDefaultVertexStyle());
            }
            if (cell.isEdge()) {
                cell.setStyle(this.getDefaultEdgeStyle());
            }
        });

        currentCell.setStyle(this.getHiglightedVertexStyle());

        currentCell.getEdges().forEach((edge: Cell) => {
            edge.target.setStyle(this.getHiglightedVertexStyle());
            edge.setStyle(this.getHighlightedEdgeStyle());
        });

        this.graph.refresh();
    }

    executeLayout(graph, layout, change, post) {
        let v1 = null;
        graph.getDataModel().beginUpdate();
        try {
            if (change != null) {
                change();
            }
            layout.execute(graph.getDefaultParent(), v1);
        } catch (e) {
            throw e;
        } finally {
            // New API for animating graph layout results asynchronously
            const morph = new Morphing(graph);
            morph.addListener(InternalEvent.DONE, () => {
                graph.getDataModel().endUpdate();
                if (post != null) {
                    post();
                }
            });
            morph.startAnimation();
        }
    }
}

class EventDiagramGraph extends Graph {}

function getGraph(container) {
    const graph = new EventDiagramGraph(container);

    return graph;
}
